import { css, cx } from "styled-system/css";
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Menu, X } from "lucide-react";
import { cf } from "@/components/brand/sfturnos-styles";
import { Logo } from "@/components/brand/logo";
import { DashboardNotificationBell } from "@/components/dashboard/dashboard-notification-bell";
import { DashboardNav } from "@/components/navigation/dashboard-nav";
import { DashboardAnnouncements } from "@/components/dashboard/dashboard-announcements";
import { TrialExpiredModal } from "@/components/dashboard/trial-expired-modal";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { computeRealStatus } from "@/lib/trial-days";

type PlanUsage = {
  bookingsUsed: number;
  bookingsLimit: number;
  agendasUsed: number;
  agendasLimit: number;
};

function usageLine(used: number, limit: number) {
  if (limit < 0) return `${used} / Ilimitado`;
  return `${used} / ${limit}`;
}

/** Reservas es el único contador que resetea mensualmente (relevante solo para plan Free, que
 *  tiene tope de 100/mes) — es siempre el 1° del mes calendario para todos los tenants, no está
 *  atado a currentPeriodEnd de ninguna suscripción. Agendas es una capacidad estática del plan,
 *  no una cuota temporal, así que no lleva esta leyenda. */
const PLAN_RESET_LABEL = "Se reinicia el 1 de cada mes";

/** Días de calendario locales hasta la fecha de vencimiento (inclusive del último día). */
function calendarDaysUntil(iso: string): number {
  const end = new Date(iso);
  const endLocal = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const now = new Date();
  const startLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((endLocal.getTime() - startLocal.getTime()) / 86400000);
}

/** Contenido del sidebar, reutilizable en desktop y en el drawer mobile. */
function SidebarContent({
  name,
  usage,
  trialDaysLeft,
  publicBookingEnabled,
  onSignOut,
  onClose,
}: {
  name: string;
  usage: PlanUsage | null;
  /** Días de prueba restantes (plan Pro/Enterprise con vencimiento futuro y sin pago pendiente). */
  trialDaysLeft: number | null;
  /** false = el link público de reservas muestra la pantalla de contacto en vez del formulario. */
  publicBookingEnabled: boolean;
  onSignOut: () => void;
  onClose?: () => void;
}) {
  return (
    <div className={cx(cf.darkPanelInner, css({ flex: "1", display: "flex", flexDir: "column", gap: "4" }))}>
      {/* ── Header: logo + campana + (X si es drawer) ── */}
      <div className={css({ minW: 0 })}>
        <div
          className={css({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "2",
            mb: "2",
          })}
        >
          <Logo onDark size={22} textSize="14px" />

          <div className={css({ display: "flex", alignItems: "center", gap: "1.5", flexShrink: 0 })}>
            <DashboardNotificationBell />
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar menú"
                className={css({
                  w: "8",
                  h: "8",
                  rounded: "lg",
                  bg: "rgba(255,255,255,0.08)",
                  borderWidth: "1px",
                  borderColor: "rgba(255,255,255,0.16)",
                  color: "rgba(255,255,255,0.7)",
                  cursor: "pointer",
                  fontSize: "md",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                  _hover: { opacity: 0.72 },
                  _active: { transform: "scale(0.93)" },
                })}
              >
                ✕
              </button>
            ) : null}
          </div>
        </div>

        <p
          className={css({
            fontSize: "xs",
            fontWeight: "medium",
            color: "rgba(255,255,255,0.68)",
            lineHeight: "1.35",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            m: 0,
          })}
          title={name || undefined}
        >
          {name || "…"}
        </p>
        {!publicBookingEnabled ? (
          <Link
            to="/dashboard/settings"
            className={css({
              display: "inline-flex",
              alignItems: "center",
              gap: "1.5",
              mt: "1.5",
              fontSize: "10px",
              fontWeight: "600",
              color: "rgba(254,215,170,0.9)",
              bg: "rgba(251,146,60,0.12)",
              borderWidth: "1px",
              borderColor: "rgba(251,146,60,0.28)",
              rounded: "full",
              px: "2",
              py: "0.5",
              textDecoration: "none",
              _hover: { bg: "rgba(251,146,60,0.18)" },
            })}
          >
            <span className={css({ w: "1.5", h: "1.5", rounded: "full", bg: "rgba(251,146,60,0.9)", flexShrink: 0 })} />
            Reservas online desactivadas
          </Link>
        ) : null}
      </div>

      <DashboardNav />

      {usage || trialDaysLeft !== null ? (
        <div
          className={css({
            rounded: "lg",
            px: "3",
            py: "3",
            bg: "rgba(255,255,255,0.05)",
            borderWidth: "1px",
            borderColor: "rgba(255,255,255,0.08)",
          })}
        >
          {usage ? (
            <div className={css({ display: "flex", flexDir: "column", gap: "2.5" })}>
              <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                <div className={css({ display: "flex", justifyContent: "space-between", alignItems: "center" })}>
                  <span className={css({ fontSize: "xs", color: "rgba(255,255,255,0.45)" })}>Reservas</span>
                  <span className={css({ fontSize: "xs", fontWeight: "600", color: "rgba(255,255,255,0.8)", fontVariantNumeric: "tabular-nums" })}>
                    {usageLine(usage.bookingsUsed, usage.bookingsLimit)}
                  </span>
                </div>
                <p className={css({ fontSize: "10px", color: "rgba(255,255,255,0.35)", lineHeight: "1.4" })}>
                  {PLAN_RESET_LABEL}
                </p>
              </div>
              <div className={css({ display: "flex", justifyContent: "space-between", alignItems: "center" })}>
                <span className={css({ fontSize: "xs", color: "rgba(255,255,255,0.45)" })}>Agendas</span>
                <span className={css({ fontSize: "xs", fontWeight: "600", color: "rgba(255,255,255,0.8)", fontVariantNumeric: "tabular-nums" })}>
                  {usageLine(usage.agendasUsed, usage.agendasLimit)}
                </span>
              </div>
            </div>
          ) : null}
          {trialDaysLeft !== null ? (
            <p
              className={css({
                fontSize: "xs",
                color: "rgba(254,240,138,0.9)",
                mt: usage ? "2" : "0",
                py: "2",
                px: "2",
                rounded: "md",
                lineHeight: "1.45",
                bg: "rgba(251,191,36,0.1)",
                borderWidth: "1px",
                borderColor: "rgba(251,191,36,0.28)",
              })}
            >
              <strong>Prueba:</strong>{" "}
              {trialDaysLeft === 0
                ? "último día"
                : trialDaysLeft === 1
                  ? "te queda 1 día"
                  : `te quedan ${trialDaysLeft} días`}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className={cx(cf.darkPanelInner, css({ mt: "auto" }))}>
        <button
          type="button"
          onClick={onSignOut}
          className={css({
            fontSize: "sm",
            color: "rgba(255,255,255,0.38)",
            bg: "transparent",
            border: "none",
            px: "2",
            py: "2",
            rounded: "md",
            cursor: "pointer",
            width: "100%",
            textAlign: "left",
            transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
            _hover: { opacity: 0.7 },
            _active: { transform: "scale(0.97)" },
          })}
        >
          Salir
        </button>
      </div>
    </div>
  );
}

export function DashboardLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, refresh } = useAuth();
  const isImpersonating = Boolean(user?.impersonatedBy);
  const calendarFullWidth = pathname === "/dashboard/calendar";
  const odontogramEditorPage = /^\/dashboard\/odontogram\/[^/]+$/.test(pathname);
  const [name, setName] = useState("");
  const [plan, setPlan] = useState<string>("FREE");
  const [pendingPlanPayment, setPendingPlanPayment] = useState(false);
  const [usage, setUsage] = useState<PlanUsage | null>(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null);
  const [publicBookingEnabled, setPublicBookingEnabled] = useState(true);
  const [gateReady, setGateReady] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [trialExpiredModalOpen, setTrialExpiredModalOpen] = useState(false);
  const professionalIdRef = useRef("");
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    void (async () => {
      const r = await fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh);
      if (r.status === 401 || r.status === 403) {
        navigate("/auth/login");
        return;
      }
      if (!r.ok) {
        setGateReady(true);
        return;
      }
      const d = (await r.json()) as {
        professional?: {
          id?: string;
          name: string;
          plan?: string;
          onboardingCompleted?: boolean;
          pendingPlanPayment?: boolean;
          planExpiresAt?: string | null;
          isActive?: boolean;
          publicBookingEnabled?: boolean;
        };
        bookingsUsed?: number;
        bookingsLimit?: number;
        agendasUsed?: number;
        agendasLimit?: number;
      };
      if (d.professional?.onboardingCompleted === false) {
        navigate("/onboarding", { replace: true });
        return;
      }
      setName(d.professional?.name ?? "");
      setPlan(d.professional?.plan ?? "FREE");
      setPendingPlanPayment(Boolean(d.professional?.pendingPlanPayment));
      setPublicBookingEnabled(d.professional?.publicBookingEnabled ?? true);

      const p = d.professional;
      let trialLeft: number | null = null;
      if (
        p &&
        (p.plan === "PRO" || p.plan === "ENTERPRISE") &&
        !p.pendingPlanPayment &&
        p.planExpiresAt
      ) {
        const left = calendarDaysUntil(p.planExpiresAt);
        if (left >= 0) trialLeft = left;
      }
      setTrialDaysLeft(trialLeft);

      if (p?.id) professionalIdRef.current = p.id;
      if (
        p?.id &&
        computeRealStatus(p.plan ?? "FREE", p.planExpiresAt ?? null, p.isActive ?? true) === "trial_expired"
      ) {
        const dismissKey = `sfturnos_trial_expired_modal_dismissed_${p.id}`;
        if (!window.sessionStorage.getItem(dismissKey)) {
          setTrialExpiredModalOpen(true);
        }
      }

      if (
        typeof d.bookingsUsed === "number" &&
        typeof d.bookingsLimit === "number" &&
        typeof d.agendasUsed === "number" &&
        typeof d.agendasLimit === "number"
      ) {
        setUsage({
          bookingsUsed: d.bookingsUsed,
          bookingsLimit: d.bookingsLimit,
          agendasUsed: d.agendasUsed,
          agendasLimit: d.agendasLimit,
        });
      }
      setGateReady(true);
    })();
  }, [navigate, refresh]);

  useEffect(() => {
    if (mobileNavOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);

  // Cierra el drawer al navegar
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  function dismissTrialExpiredModal() {
    if (professionalIdRef.current) {
      window.sessionStorage.setItem(`sfturnos_trial_expired_modal_dismissed_${professionalIdRef.current}`, "1");
    }
    setTrialExpiredModalOpen(false);
  }

  async function signOut() {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    navigate("/");
    window.location.reload();
  }

  async function exitImpersonation() {
    await fetch("/api/auth/impersonate/exit", { method: "POST", credentials: "include" });
    window.location.href = "/admin/planes";
  }

  return (
    <>
      <TrialExpiredModal open={trialExpiredModalOpen} onDismiss={dismissTrialExpiredModal} plan={plan} />

      {isImpersonating ? (
        <div
          role="status"
          className={css({
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "3",
            flexWrap: "wrap",
            px: "4",
            py: "2.5",
            bg: "#7c2d12",
            color: "white",
            fontSize: "sm",
            borderTopWidth: "1px",
            borderColor: "rgba(255,255,255,0.14)",
            boxShadow: "0 -4px 16px rgba(0,0,0,0.18)",
          })}
        >
          <span>
            Estás viendo la cuenta de <strong>{name || "…"}</strong> como superadmin.
          </span>
          <button
            type="button"
            onClick={() => void exitImpersonation()}
            className={css({
              fontSize: "xs",
              fontWeight: "semibold",
              color: "#7c2d12",
              bg: "white",
              border: "none",
              px: "3",
              py: "1",
              rounded: "md",
              cursor: "pointer",
              transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1), opacity 150ms ease",
              _hover: { opacity: 0.92 },
              _active: { transform: "scale(0.97)" },
            })}
          >
            Salir
          </button>
        </div>
      ) : null}

      {/* ── Barra superior fija · mobile + tablet, <1024px (z-40, queda BAJO el overlay) ── */}
      <div
        className={css({
          display: { base: "flex", lg: "none" },
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 40,        // deliberadamente bajo para que el overlay (z-50) lo tape
          h: "16",
          alignItems: "center",
          justifyContent: "space-between",
          px: "4",
          bg: "#0b1220",
          borderBottomWidth: "1px",
          borderColor: "rgba(255,255,255,0.08)",
        })}
      >
        <Logo onDark size={26} textSize="14px" />
        <button
          type="button"
          aria-label={mobileNavOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((v) => !v)}
          className={css({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            w: "10",
            h: "10",
            rounded: "lg",
            bg: mobileNavOpen ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.08)",
            borderWidth: "1px",
            borderColor: mobileNavOpen ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.16)",
            color: "rgba(255,255,255,0.92)",
            cursor: "pointer",
            transition: "background 120ms ease, border-color 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
            _hover: { opacity: 0.85 },
            _active: { transform: "scale(0.93)" },
          })}
        >
          {mobileNavOpen ? <X size={18} aria-hidden /> : <Menu size={18} aria-hidden />}
        </button>
      </div>

      {/* ── Drawer overlay animado · solo mobile (z-50, tapa la top bar) ── */}
      <AnimatePresence>
        {mobileNavOpen ? (
          <motion.div
            key="mobile-drawer-root"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 50,   // tapa la top bar (z-40)
              display: "flex",
              alignItems: "stretch",
            }}
          >
            {/* Backdrop — cubre TODO incluyendo la top bar */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.6)",
                backdropFilter: "blur(2px)",
                WebkitBackdropFilter: "blur(2px)",
              }}
              onClick={() => setMobileNavOpen(false)}
            />

            {/* Panel lateral deslizante */}
            <motion.aside
              initial={{ x: shouldReduceMotion ? 0 : "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: shouldReduceMotion ? 0 : "-100%" }}
              transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
              className={cx(
                cf.darkPanel,
                css({
                  position: "relative",
                  zIndex: 1,
                  w: "72",
                  maxW: "82vw",
                  h: "100%",
                  p: "5",
                  display: "flex",
                  flexDir: "column",
                  overflowY: "auto",
                }),
              )}
            >
              <SidebarContent
                name={name}
                usage={usage}
                trialDaysLeft={trialDaysLeft}
                publicBookingEnabled={publicBookingEnabled}
                onSignOut={() => void signOut()}
                onClose={() => setMobileNavOpen(false)}
              />
            </motion.aside>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* ── Layout grid principal ──────────────────────────────────── */}
      <div
        className={css({
          minH: "100vh",
          display: "grid",
          gridTemplateColumns: { base: "1fr", lg: "260px 1fr" },
        })}
      >
        {/* Sidebar desktop: oculto en mobile/tablet (<1024px), sticky en desktop */}
        <aside
          className={cx(
            cf.darkPanel,
            css({
              display: { base: "none", lg: "flex" },
              p: "5",
              position: "sticky",
              top: "0",
              h: "100vh",
              flexDir: "column",
              borderRightWidth: "1px",
              borderColor: "rgba(255,255,255,0.08)",
            }),
          )}
        >
          <SidebarContent
            name={name}
            usage={usage}
            trialDaysLeft={trialDaysLeft}
            publicBookingEnabled={publicBookingEnabled}
            onSignOut={() => void signOut()}
          />
        </aside>

        {/* Área de contenido */}
        <div
          className={cf.mainArea}
          style={odontogramEditorPage ? { padding: 0 } : undefined}
        >
          {/* Espaciador mobile/tablet para la top bar fija */}
          <div className={css({ h: { base: "16", lg: "0" } })} />
          <div
            className={cx(
              calendarFullWidth || odontogramEditorPage
                ? css({ w: "100%", maxW: "none", mx: "0" })
                : cf.contentMax,
              !odontogramEditorPage && css({ pb: "16" }),
            )}
          >
            <DashboardAnnouncements />
            {gateReady && pendingPlanPayment && (plan === "PRO" || plan === "ENTERPRISE") ? (
              <div
                className={css({
                  mb: "4",
                  p: "4",
                  rounded: "lg",
                  borderWidth: "1px",
                  borderColor: "amber.200",
                  bg: "amber.50",
                })}
                role="status"
              >
                <p className={css({ fontSize: "sm", color: "amber.900", lineHeight: "1.5", m: 0 })}>
                  <strong>Pendiente:</strong> regularizá el pago de tu plan {plan === "PRO" ? "Pro" : "Enterprise"} con SFTURNOS. Revisá la
                  sección de suscripción o escribinos a facturación.
                </p>
                <a
                  href="/dashboard/subscription"
                  className={css({
                    display: "inline-block",
                    mt: "2",
                    fontSize: "sm",
                    fontWeight: "semibold",
                    color: "token(colors.brand.600)",
                    textDecoration: "underline",
                  })}
                >
                  Ir a suscripción
                </a>{" "}
                <a
                  href="mailto:turnos@sftdevelopment.com?subject=Plan%20SFTURNOS%20—%20pago"
                  className={css({
                    display: "inline-block",
                    mt: "2",
                    ml: "2",
                    fontSize: "sm",
                    color: "amber.900",
                    textDecoration: "underline",
                  })}
                >
                  turnos@sftdevelopment.com
                </a>
              </div>
            ) : null}
            {gateReady ? <Outlet context={{ plan }} /> : <LoadingFrame variant="light" minHeight={320} />}
          </div>
        </div>
      </div>
    </>
  );
}
